// src/components/pipelines/nodes/EntryNode.jsx - Entry Point Node for React Flow
import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card.tsx';
import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Button } from '../../ui/button.tsx';
import { Play, Calendar, UserPlus, MousePointerClick, Edit2, Plus, X } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';

/**
 * EntryNode - Represents the start point of an automation flow
 *
 * Node data structure:
 * {
 *   id: 'entry-uuid',
 *   type: 'entry',
 *   data: {
 *     triggerType: 'system_event' | 'time_based' | 'manual',
 *     eventType: 'customer_created' | 'invoice_paid' | etc (if system_event),
 *     conditions: [{field: '...', operator: '...', value: '...'}],
 *     description: 'Entry point description'
 *   }
 * }
 */
const EntryNode = ({ data, selected, id }) => {
  const {
    triggerType = 'system_event',
    eventType,
    conditions = [],
    description
  } = data;
  const { updateNodeData } = useReactFlow();

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description || '');
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [newCondition, setNewCondition] = useState({ field: '', operator: 'equals', value: '' });

  // Get icon and label based on trigger type
  const getTriggerConfig = () => {
    switch (triggerType) {
      case 'system_event':
        return {
          icon: UserPlus,
          label: 'Event Trigger',
          color: 'green',
          detail: eventType ? eventType.replace(/_/g, ' ') : 'System event'
        };
      case 'time_based':
        return {
          icon: Calendar,
          label: 'Scheduled',
          color: 'blue',
          detail: 'Time-based trigger'
        };
      case 'manual':
        return {
          icon: MousePointerClick,
          label: 'Manual',
          color: 'purple',
          detail: 'Manually triggered'
        };
      default:
        return {
          icon: Play,
          label: 'Start',
          color: 'green',
          detail: 'Entry point'
        };
    }
  };

  const config = getTriggerConfig();
  const Icon = config.icon;

  // Update trigger type
  const handleTriggerTypeChange = (newType) => {
    updateNodeData(id, { ...data, triggerType: newType });
  };

  // Update event type
  const handleEventTypeChange = (newEventType) => {
    updateNodeData(id, { ...data, eventType: newEventType });
  };

  // Save description
  const handleSaveDescription = () => {
    updateNodeData(id, { ...data, description: editedDescription.trim() });
    setIsEditingDescription(false);
  };

  // Add condition
  const handleAddCondition = () => {
    if (newCondition.field && newCondition.value) {
      const updatedConditions = [...conditions, newCondition];
      updateNodeData(id, { ...data, conditions: updatedConditions });
      setNewCondition({ field: '', operator: 'equals', value: '' });
      setIsAddingCondition(false);
    }
  };

  // Remove condition
  const handleRemoveCondition = (index) => {
    const updatedConditions = conditions.filter((_, i) => i !== index);
    updateNodeData(id, { ...data, conditions: updatedConditions });
  };

  return (
    <div className="entry-node">
      {/* No input handle - this is the start */}

      <Card className={`
        w-56
        ${selected ? `ring-2 ring-${config.color}-500 shadow-lg` : 'shadow-md'}
        transition-all duration-200
        hover:shadow-xl
        bg-gradient-to-br from-${config.color}-500/10 to-${config.color}-600/5
        border-${config.color}-500/20
      `}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className={`text-base font-semibold text-${config.color}-100 flex items-center gap-2`}>
              <Icon className="w-4 h-4" />
              Entry
            </CardTitle>
            <Select value={triggerType} onValueChange={handleTriggerTypeChange}>
              <SelectTrigger className={`w-32 h-6 text-xs border-${config.color}-500/50 text-${config.color}-300 no-drag`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="system_event" className="text-slate-200">Event</SelectItem>
                <SelectItem value="time_based" className="text-slate-200">Scheduled</SelectItem>
                <SelectItem value="manual" className="text-slate-200">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-2">
          {/* Event Type (if system event) */}
          {triggerType === 'system_event' && (
            <Select value={eventType || ''} onValueChange={handleEventTypeChange}>
              <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200 no-drag">
                <SelectValue placeholder="Select event..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="customer_created" className="text-slate-200">Customer Created</SelectItem>
                <SelectItem value="invoice_paid" className="text-slate-200">Invoice Paid</SelectItem>
                <SelectItem value="invoice_sent" className="text-slate-200">Invoice Sent</SelectItem>
                <SelectItem value="task_completed" className="text-slate-200">Task Completed</SelectItem>
                <SelectItem value="tag_added" className="text-slate-200">Tag Added</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Conditions List */}
          {conditions.length > 0 && (
            <div className="space-y-1">
              {conditions.map((condition, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-xs bg-slate-800/50 rounded px-2 py-1 border border-slate-700/50 no-drag"
                >
                  <div className="flex-1">
                    <span className="text-slate-300">{condition.field}</span>
                    <span className="text-slate-500 mx-1">{condition.operator}</span>
                    <span className="text-purple-300">{String(condition.value)}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveCondition(index)}
                    className="h-4 w-4 p-0 text-red-400 hover:text-red-300"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add Condition */}
          {isAddingCondition ? (
            <div className="space-y-2 p-2 bg-slate-800/30 rounded border border-slate-700/50 no-drag">
              <Input
                placeholder="Field name..."
                value={newCondition.field}
                onChange={(e) => setNewCondition({ ...newCondition, field: e.target.value })}
                className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200"
              />
              <Select
                value={newCondition.operator}
                onValueChange={(val) => setNewCondition({ ...newCondition, operator: val })}
              >
                <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="equals" className="text-slate-200">Equals</SelectItem>
                  <SelectItem value="not_equals" className="text-slate-200">Not Equals</SelectItem>
                  <SelectItem value="contains" className="text-slate-200">Contains</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Value..."
                value={newCondition.value}
                onChange={(e) => setNewCondition({ ...newCondition, value: e.target.value })}
                className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAddCondition}
                  className="flex-1 h-6 text-xs bg-green-600 hover:bg-green-700"
                >
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsAddingCondition(false);
                    setNewCondition({ field: '', operator: 'equals', value: '' });
                  }}
                  className="flex-1 h-6 text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAddingCondition(true)}
              className={`w-full h-7 text-xs border-${config.color}-500/30 text-${config.color}-300 hover:bg-${config.color}-500/10 no-drag`}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Condition
            </Button>
          )}

          {/* Editable Description */}
          <div className="border-t border-slate-700/50 pt-2">
            {isEditingDescription ? (
              <Input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={handleSaveDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDescription();
                  if (e.key === 'Escape') setIsEditingDescription(false);
                }}
                placeholder="Add description..."
                autoFocus
                className="text-xs bg-slate-800 border-slate-700 text-slate-200 no-drag"
              />
            ) : (
              <div
                className="flex items-center gap-2 justify-center cursor-pointer no-drag"
                onClick={() => {
                  setEditedDescription(description || '');
                  setIsEditingDescription(true);
                }}
              >
                {description ? (
                  <div className="text-xs text-slate-400 italic">{description}</div>
                ) : (
                  <div className="text-xs text-slate-500 italic">Add description...</div>
                )}
                <Edit2 className="w-3 h-3 text-slate-500 hover:text-slate-300" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Output handle (right side) - connects to first workflow node */}
      <Handle
        type="source"
        position={Position.Right}
        className={`w-3 h-3 !bg-${config.color}-500`}
      />
    </div>
  );
};

export default EntryNode;
